// src/pages/cart.jsx
import React, { memo } from "react";
import { Trash2, Minus, Plus, ShoppingCart } from 'lucide-react';
import { Link } from "react-router-dom";

// Import the useCart hook
import { useCart } from '../context/cartcontext';

// Styling Constants
const PRIMARY_COLOR = "text-teal-400";
const ACCENT_BG = "bg-teal-600 hover:bg-teal-500";
const TEXT_COLOR = 'text-gray-100';
const DEFAULT_IMAGE_PATH = "/media/products/default.jpg";

// ------------------------------
// Single Cart Item Component
// ------------------------------
const CartItem = memo(({ item, onUpdateQuantity, onRemoveItem }) => {
  const product = item.product || {};
  const quantity = item.quantity || 1;
  const MAX_PER_PRODUCT = 10;

  const handleIncrease = () => onUpdateQuantity(product.id, Math.min(quantity + 1, MAX_PER_PRODUCT));
  const handleDecrease = () => quantity > 1 && onUpdateQuantity(product.id, quantity - 1);
  const handleRemove = () => onRemoveItem(product.id);

  const imageUrl = product.image
    ? (product.image.startsWith('/') ? product.image : `/${product.image}`)
    : DEFAULT_IMAGE_PATH;

  const categoryText = product.category
    ? (product.category.name || product.category)
    : 'N/A';

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between p-4 mb-4 bg-gray-800 rounded-xl shadow-md border border-gray-700">

      {/* Left Side */}
      <div className="flex items-center space-x-4 flex-grow w-full sm:w-auto">
        <Link to={`/product/${product.slug || ''}`} className="flex-shrink-0">
          <img
            src={imageUrl}
            alt={product.name || "Unnamed Product"}
            className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md border border-teal-600"
            onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_IMAGE_PATH; }}
          />
        </Link>

        <div className="min-w-0 flex-grow">
          <Link
            to={`/product/${product.slug || ''}`}
            className={`text-base sm:text-lg font-semibold ${TEXT_COLOR} hover:text-teal-400 transition-colors truncate`}
          >
            {product.name || "Unnamed Product"}
          </Link>
          <p className="text-gray-400 text-sm">{categoryText}</p>
        </div>
      </div>

      {/* Right Side */}
      <div className="flex items-center space-x-4 justify-end w-full sm:w-auto mt-4 sm:mt-0 flex-wrap">

        {/* Quantity Controls */}
        <div className="flex items-center space-x-1">
          <button
            onClick={handleDecrease}
            className="w-6 h-6 flex items-center justify-center border border-gray-600 rounded-md text-gray-400 hover:bg-gray-700"
          >
            <Minus className="w-3 h-3" />
          </button>

          <input
            type="number"
            value={quantity}
            readOnly
            className="w-8 text-center bg-gray-700 text-white rounded-md text-xs"
          />

          <button
            onClick={handleIncrease}
            disabled={quantity >= MAX_PER_PRODUCT}
            className={`w-6 h-6 flex items-center justify-center border border-gray-600 rounded-md text-gray-400 hover:bg-gray-700 ${
              quantity >= MAX_PER_PRODUCT ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>

        {/* Price */}
        <p className={`font-bold text-lg sm:text-xl ${PRIMARY_COLOR} min-w-[60px] text-right`}>
          £{(parseFloat(product.price || 0) * quantity).toFixed(2)}
        </p>

        {/* Remove Button */}
        <button
          onClick={handleRemove}
          className="text-red-500 hover:text-red-400 p-2 rounded-lg transition-colors flex-shrink-0"
          title="Remove item"
        >
          <Trash2 className="w-5 h-5" />
        </button>

      </div>
    </div>
  );
});

// ------------------------------
// Main Cart Page
// ------------------------------
const Cart = () => {
  const {
    cartItems,
    updateItemQuantity,
    removeFromCart,
    emptyCart
  } = useCart();

  const subtotal = cartItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shipping = 0;
  const total = subtotal + shipping;

  return (
    <div className="min-h-screen pt-32 bg-gray-900 px-4 sm:px-6 lg:px-8 font-inter">
      <div className="max-w-7xl mx-auto">

        <h1 className={`text-4xl sm:text-5xl font-extrabold ${PRIMARY_COLOR} mb-12 border-b border-teal-700 pb-3`}>
          Your Shopping Cart
        </h1>

        {cartItems.length === 0 ? (
          <div className="text-center py-20 bg-gray-800 rounded-xl border border-gray-700">
            <ShoppingCart className={`w-16 h-16 mx-auto mb-4 ${PRIMARY_COLOR}`} />
            <p className="text-2xl text-white mb-4">Your cart is empty.</p>
            <Link to="/shop" className={`${ACCENT_BG} text-white px-6 py-3 rounded-xl font-semibold`}>
              Start Shopping
            </Link>
          </div>
        ) : (

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="flex justify-end mb-4">
                <button
                  onClick={emptyCart}
                  className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-semibold transition-colors"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Empty Cart
                </button>
              </div>

              {cartItems.map(item => (
                <CartItem
                  key={item.product.id}
                  item={item}
                  onUpdateQuantity={updateItemQuantity}
                  onRemoveItem={removeFromCart}
                />
              ))}
            </div>

            {/* Summary */}
            <div className="lg:col-span-1 bg-gray-800 p-6 rounded-xl shadow-lg border border-teal-700 h-fit">
              <h2 className="text-3xl font-bold text-white mb-6 border-b border-gray-700 pb-3">Order Summary</h2>

              <div className="space-y-3 text-gray-300 mb-6">
                <div className="flex justify-between">
                  <span>Subtotal ({cartItems.length} items)</span>
                  <span>£{subtotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span><span className="text-green-500 font-semibold">FREE</span></span>
                </div>

                <div className="flex justify-between pt-4 border-t border-gray-700 text-white font-extrabold text-xl">
                  <span>Order Total</span>
                  <span>£{total.toFixed(2)}</span>
                </div>
              </div>

              <Link
                to="/checkout"
                className={`w-full ${ACCENT_BG} text-white p-3 rounded-xl text-lg font-bold text-center block shadow-2xl`}
              >
                Proceed to Checkout
              </Link>

              <Link
                to="/shop"
                className="text-center block mt-4 text-sm text-gray-400 hover:text-teal-400"
              >
                Continue Shopping
              </Link>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default Cart;
